import type { IGetCargoTripRes, TTripTaskRes } from 'api/swagger';
import { extractAddressFields, extractCoordinates } from 'shared/utils/address';
import { ELocationMode } from 'types/location';
import type { TCoordinatesFields, TAddressFields } from 'types/location';
import { ETripTaskType } from 'types/trips';
import type { TCreationMode } from 'components/trips/trip-create-task-modal/types';
import { createStore } from './create-store';

export type TTripTaskState = {
  taskType?: ETripTaskType;
  creationMode: TCreationMode;
  locationMode: ELocationMode;
  coordinates: TCoordinatesFields;
  address: TAddressFields;
  warehouseId?: number;
  loadingCustomAddress: TAddressFields;
  unloadingCustomAddress: TAddressFields;
  originalTaskType?: ETripTaskType;
  cargoTrips: IGetCargoTripRes[];
};

type TActions = {
  reset: () => void;
  setTaskType: (taskType: ETripTaskType | undefined) => void;
  setCreationMode: (mode: TCreationMode) => void;
  setLocationMode: (mode: ELocationMode) => void;
  setCoordinates: (coordinates: TCoordinatesFields) => void;
  setAddress: (address: TAddressFields) => void;
  setWarehouseId: (warehouseId: number) => void;
  initializeForEdit: (tripTask: TTripTaskRes) => void;
  setCargoTrips: (cargoTrips: IGetCargoTripRes[]) => void;
  addCargoTrip: (cargoTrip: IGetCargoTripRes) => void;
  removeCargoTrip: (cargoTripId: number) => void;
  clearCargoTrips: () => void;
};

const initialAddressState: TAddressFields = {
  name: undefined,
  countryId: undefined,
  postCode: undefined,
  city: undefined,
  address: undefined,
};

const createDefaultState = (): TTripTaskState => ({
  taskType: undefined,
  creationMode: 'separate_tasks',
  locationMode: ELocationMode.address,
  coordinates: { lat: undefined, lng: undefined },
  warehouseId: undefined,
  address: { ...initialAddressState },
  loadingCustomAddress: { ...initialAddressState },
  unloadingCustomAddress: { ...initialAddressState },
  originalTaskType: undefined,
  cargoTrips: [],
});

export const useTripTaskStore = createStore<TTripTaskState & TActions>(
  (set, get) => ({
    ...createDefaultState(),

    reset: () =>
      set((state) => ({
        ...createDefaultState(),
        cargoTrips: state.cargoTrips,
      })),

    setCargoTrips: (cargoTrips) => set({ cargoTrips }),

    addCargoTrip: (selectedCargoTrip) =>
      set((state) => {
        if (state.cargoTrips.find((cargoTrip) => cargoTrip.id === selectedCargoTrip.id)) {
          return state;
        }
        return { cargoTrips: [...state.cargoTrips, selectedCargoTrip] };
      }),

    removeCargoTrip: (cargoTripId) =>
      set((state) => ({
        cargoTrips: state.cargoTrips.filter((cargoTrip) => cargoTrip.id !== cargoTripId),
      })),

    clearCargoTrips: () => set({ cargoTrips: [] }),

    setTaskType: (taskType) => {
      const { cargoTrips, originalTaskType } = get();

      if (originalTaskType) {
        set({ taskType });
        return;
      }
      const baseState = { ...createDefaultState(), cargoTrips, taskType };

      if (cargoTrips?.length === 1) {
        const cargoTrip = cargoTrips[0];
        const cargo = cargoTrip.cargo;

        if (taskType === ETripTaskType.customs) {
          const updateData: Partial<TTripTaskState> = {
            locationMode: ELocationMode.address,
          };

          if (cargo.loadingCustomAddress) {
            updateData.loadingCustomAddress = extractAddressFields(cargo.loadingCustomAddress);
          }

          if (cargo.unloadingCustomAddress) {
            updateData.unloadingCustomAddress = extractAddressFields(cargo.unloadingCustomAddress);
          }

          set({ ...baseState, ...updateData });
          return;
        }

        let initialAddress = null;

        if (taskType === ETripTaskType.loading) {
          initialAddress = cargo.loadingAddress;
        } else if (taskType === ETripTaskType.unloading) {
          initialAddress = cargo.unloadingAddress;
        } else if (taskType === ETripTaskType.return_asset) {
          initialAddress = cargo.assetReturnAddress;
        }

        if (initialAddress) {
          const updateData: Partial<TTripTaskState> = {
            address: extractAddressFields(initialAddress),
          };

          if (
            taskType === ETripTaskType.loading ||
            taskType === ETripTaskType.unloading ||
            taskType === ETripTaskType.return_asset
          ) {
            updateData.locationMode = ELocationMode.fromCargo;

            const coordinates = extractCoordinates(initialAddress);
            if (coordinates) {
              updateData.coordinates = coordinates;
            }
          }

          set({ ...baseState, ...updateData });
          return;
        }
      }

      set(baseState);
    },

    setCreationMode: (mode) => set({ creationMode: mode }),

    setLocationMode: (mode) => set({ locationMode: mode }),

    setCoordinates: (coordinates) => set({ coordinates }),

    setAddress: (address) => set({ address }),

    setWarehouseId: (warehouseId) => set({ warehouseId }),

    initializeForEdit: (tripTask) => {
      const updateData: Partial<TTripTaskState> = {
        taskType: tripTask.type as ETripTaskType,
        originalTaskType: tripTask.type as ETripTaskType,
        creationMode: 'one_task',
      };

      if (tripTask.address) {
        const isAddressExists = tripTask.address.countryId && tripTask.address.city && tripTask.address.address;

        if (isAddressExists) {
          updateData.locationMode = ELocationMode.address;
          updateData.address = extractAddressFields(tripTask.address);
        } else {
          updateData.locationMode = ELocationMode.coordinates;
        }

        const coordinates = extractCoordinates(tripTask.address);
        if (coordinates) {
          updateData.coordinates = coordinates;
        }
      }

      if (tripTask.warehouseId) {
        updateData.warehouseId = tripTask.warehouseId;
      }

      set(updateData);
    },
  }),
  'trip-task-store',
);
